// src/config/runtimeConfig.ts
import { ClientConfig } from "../app.config.base";

let cachedConfig: ClientConfig | null = null;
let isLoaded = false;

const STORAGE_KEY = "runtimeConfig";

const saveConfigToStorage = (config: ClientConfig | null): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error("Failed to save runtime config to localStorage:", error);
  }
};

const loadConfigFromStorage = (): ClientConfig | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error("Failed to load runtime config from localStorage:", error);
  }
  return null;
};

export const initializeRuntimeConfig = async (): Promise<void> => {
  if (isLoaded && cachedConfig) return;

  const savedConfig = loadConfigFromStorage();
  if (savedConfig) {
    cachedConfig = savedConfig;
    isLoaded = true;
    console.log("✅ Config loaded from localStorage");
    return;
  }

  const clientId = process.env.REACT_APP_CLIENT_ID;

  try {
    const module = await import(`../app.config.${clientId}.ts`);
    cachedConfig = module.default;
    isLoaded = true;
    saveConfigToStorage(cachedConfig);
    console.log(`✅ Config loaded for client: ${clientId}`);
  } catch (error) {
    console.error(`Failed to load config for client: ${clientId}`, error);
    throw error;
  }
};

export const getClientConfig = (): ClientConfig => {
  if (!cachedConfig) {
    const savedConfig = loadConfigFromStorage();
    if (savedConfig) {
      cachedConfig = savedConfig;
      isLoaded = true;
      return cachedConfig;
    }
    throw new Error(
      "Runtime config not loaded yet. Call initializeRuntimeConfig() first.",
    );
  }
  return cachedConfig;
};

export const isRuntimeConfigLoaded = (): boolean => isLoaded;

export const getCacheVersion = (): number => {
  return parseInt(process.env.REACT_APP_CACHE_VERSION || "1", 10);
};

export const getBaseUrl = (): string => {
  const config = getClientConfig();
  return `/sales-api/${config.apiPort}/${config.apiToken}/api/v1/data`;
};

export const getImageUri = (): string => {
  return `${getBaseUrl()}/file?path=`;
};

export const getAuthUrl = (): string => {
  return "https://logid.ru/api/v1/auth";
};

export const getBotUrl = (): string => {
  return getClientConfig().botUrl || process.env.REACT_APP_BOT_URL || "";
};

export const getApiToken = (): string => {
  return getClientConfig().apiToken;
};

export const getClientName = (): string => {
  return getClientConfig().name;
};

export const resetRuntimeConfig = (): void => {
  cachedConfig = null;
  isLoaded = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to remove runtime config from localStorage:", error);
  }
};

export const getMainScreenVariant = ():
  | "products"
  | "categories"
  | "hybrid" => {
  const config = getClientConfig();
  return config.mainScreenVariant || "products";
};

export const getBannerVariant = (): string => {
  const config = getClientConfig();
  return config.bannerVariant || "base";
};

export const getButtonsVariant = (): "circular" | "minimal" | "floating" => {
  const config = getClientConfig();
  return config.buttonsVariant || "minimal";
};

export const getCardLayout = (): "grid" | "list" => {
  const config = getClientConfig();
  return config.cardLayout || "grid";
};

export const getCardStyle = (): "detailed" | "minimal" | "flat" | "wide"=> {
  const config = getClientConfig();
  return config.cardStyle || "detailed";
};

export const getLeadFeature = (): ClientConfig["leadFeature"] | undefined => {
  return getClientConfig().leadFeature;
};

export const hasCallButton = (): boolean => {
  return getClientConfig().callButton === true;
};
